// Zweck: Buchungsprüfung ohne eigene Speicherung oder Übertragung. Stand: 21.10.2026.
import { WarehouseSteps } from './WarehousePresentation';
import type {
  WarehouseBooking,
  WarehouseDraft,
  WarehouseLocal,
  WarehouseLine,
} from '../interfaces/warehouse';
import { warehouseStock } from '../services/warehouseQueue';
interface Props {
  draft: WarehouseDraft;
  data: WarehouseLocal;
  projectLabel: (id: string | null) => string | undefined;
  effect: (draft: WarehouseBooking, line: WarehouseLine) => number;
  hasList: boolean;
  labels: Record<string, string>;
  offline: boolean;
  connected: boolean;
  busy: boolean;
  onEdit: () => void;
  onSubmit: () => void;
}
export default function WarehouseQuantityReview({
  draft,
  data,
  projectLabel,
  effect,
  hasList,
  labels,
  offline,
  connected,
  busy,
  onEdit,
  onSubmit,
}: Props) {
  const snapshot = data.snapshot!;
  const article = (id: string) => snapshot.articles.find((a) => a.id === id);
  const number = (n: number) =>
    n.toLocaleString('de-DE', { maximumFractionDigits: 3 });
  return (
    <section className="wh-review" aria-labelledby="wh-review-title">
      <WarehouseSteps step={3} />
      <button
        className="wh-back wh-review-back"
        aria-label="Mengen bearbeiten"
        disabled={busy}
        onClick={onEdit}
      >
        <span aria-hidden="true">← </span>Mengen bearbeiten
      </button>
      <h2 id="wh-review-title" tabIndex={-1}>
        {draft.kind === 'issue'
          ? 'Ausgabe prüfen'
          : draft.kind === 'return'
            ? 'Rückgabe prüfen'
            : draft.kind === 'correction'
              ? 'Korrektur prüfen'
              : `${labels[draft.kind]} prüfen`}
      </h2>
      <p className="wh-field-hint">
        Noch nichts gebucht. Stimmen diese Mengen?
      </p>
      <p className="wh-context">
        {snapshot.locations.find((l) => l.id === draft.locationId)?.name} ·{' '}
        {projectLabel(draft.projectId) ?? draft.reason ?? 'Ohne Auftrag'}
      </p>
      {draft.lines.map((l) => (
        <div className="wh-review-line" key={l.articleId}>
          <div className="wh-review-item">
            <div>
              <strong>{article(l.articleId)?.name}</strong>
              <p className="wh-field-hint">
                Artikelnr. {article(l.articleId)?.number}
              </p>
            </div>
            <strong className="wh-review-quantity">
              {number(l.quantity)} {article(l.articleId)?.unit}
            </strong>
          </div>
          {draft.kind === 'correction' && (
            <p>
              {l.addInsteadOfReverse
                ? 'Fehlende Menge ergänzen'
                : 'Zu viel gebuchte Menge zurücknehmen'}
            </p>
          )}
          <p className="wh-review-stock">
            Bestand am Lagerort:{' '}
            {number(warehouseStock(data, l.articleId, draft.locationId))} →{' '}
            {number(
              warehouseStock(data, l.articleId, draft.locationId) +
                effect(draft, l),
            )}{' '}
            {article(l.articleId)?.unit}
          </p>
          {l.negativeReason && (
            <p>Grund für Minusbestand: {l.negativeReason}</p>
          )}
        </div>
      ))}
      {hasList && (
        <p>
          Nicht ausgewählte Positionen bleiben offen. Bei Teilmengen bleibt die
          Restmenge offen.
        </p>
      )}
      <div className="wh-review-confirm">
        <strong>{draft.lines.length} Artikel bestätigen</strong>
        <p>
          {draft.kind === 'issue'
            ? 'Bestätige nur, was du tatsächlich aus diesem Lager entnommen hast.'
            : draft.kind === 'receipt'
              ? 'Bestätige nur, was tatsächlich im Lager angekommen ist.'
              : draft.kind === 'return'
                ? 'Bestätige nur, was du tatsächlich zurück ins Lager gelegt hast.'
                : 'Bestätige die tatsächlich gezählten Mengen.'}
        </p>
        {(offline || !connected) && (
          <p className="wh-notice">
            Ohne Verbindung: Die Liste bleibt auf diesem Gerät. Sie wird
            übertragen, wenn du die App mit Internet und deinem Konto öffnest.
            Nicht erneut buchen.
          </p>
        )}
        <button disabled={busy} className="wh-primary" onClick={onSubmit}>
          {busy
            ? 'Wird gespeichert …'
            : draft.kind === 'issue'
              ? 'Ausgabe bestätigen'
              : 'Buchung bestätigen'}
        </button>
      </div>
    </section>
  );
}
